import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useEventMeta } from "@/lib/event-meta";
import StatusSemanticNote from "@/components/StatusSemanticNote";
import { API_BASE } from "@/lib/utils";

// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

function emptySession() {
  return {
    id: `s_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    title: "",
    room: "",
    time: "",
    capacity: 0,
    registered: 0,
  };
}

export default function ScheduleManager() {
  const { id } = useParams();
  const eventMeta = useEventMeta(id);
  const resolvedEventId = String(id || "").startsWith("evt_") ? id : eventMeta.id;
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!resolvedEventId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/events/${resolvedEventId}/sessions`);
        if (!res.ok) throw new Error(`Failed to load sessions (${res.status})`);
        const payload = await res.json();
        if (!cancelled) {
          setSessions(Array.isArray(payload.sessions) ? payload.sessions : []);
          setError("");
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || "Failed to load sessions");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedEventId]);

  const updateSession = (sid, patch) =>
    setSessions((prev) => prev.map((row) => (row.id === sid ? { ...row, ...patch } : row)));

  const addSession = () => setSessions((prev) => [...prev, emptySession()]);
  const removeSession = (sid) => setSessions((prev) => prev.filter((row) => row.id !== sid));

  const save = async () => {
    if (!resolvedEventId) {
      toast.error("Event is still loading. Please try again.");
      return;
    }
    try {
      setSaving(true);
      const clean = sessions.map((row) => ({
        id: String(row.id || ""),
        title: String(row.title || ""),
        room: String(row.room || ""),
        time: String(row.time || ""),
        capacity: Number(row.capacity || 0),
        registered: Number(row.registered || 0),
      }));
      const res = await fetch(`${API_BASE}/api/admin/events/${resolvedEventId}/sessions`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ sessions: clean }),
      });
      if (!res.ok) throw new Error(`Failed to save sessions (${res.status})`);
      const payload = await res.json();
      setSessions(Array.isArray(payload.sessions) ? payload.sessions : clean);
      toast.success("Schedule saved");
      setError("");
    } catch (err) {
      setError(err?.message || "Failed to save sessions");
      toast.error("Could not save schedule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1500px] mx-auto" data-testid="schedule-manager">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-overline">{eventMeta.title} · schedule</div>
          <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Schedule manager</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2">Edit sessions used in overview and page builder blocks.</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" type="button" onClick={addSession}><Plus size={14} /> Add session</button>
          <button className="btn-primary" type="button" onClick={save} disabled={saving || loading || !resolvedEventId}><Save size={14} /> {saving ? "Saving..." : "Save schedule"}</button>
        </div>
      </div>

      {loading ? <StatusSemanticNote state="loading" message="Loading sessions. Save is disabled until the schedule is ready." /> : null}

      <div className="mt-8 surface overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-4 text-left">Title</th>
              <th className="p-4 text-left">Time</th>
              <th className="p-4 text-left">Room</th>
              <th className="p-4 text-right">Capacity</th>
              <th className="p-4 text-right">Registered</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((row) => (
              <tr key={row.id} className="border-b border-[var(--border-default)]">
                <td className="p-4"><input className="input-dark" value={row.title || ""} onChange={(e) => updateSession(row.id, { title: e.target.value })} /></td>
                <td className="p-4"><input className="input-dark" value={row.time || ""} onChange={(e) => updateSession(row.id, { time: e.target.value })} /></td>
                <td className="p-4"><input className="input-dark" value={row.room || ""} onChange={(e) => updateSession(row.id, { room: e.target.value })} /></td>
                <td className="p-4 text-right"><input className="input-dark text-right font-mono" type="number" min="0" value={Number(row.capacity || 0)} onChange={(e) => updateSession(row.id, { capacity: Number(e.target.value || 0) })} /></td>
                <td className="p-4 text-right"><input className="input-dark text-right font-mono" type="number" min="0" value={Number(row.registered || 0)} onChange={(e) => updateSession(row.id, { registered: Number(e.target.value || 0) })} /></td>
                <td className="p-4 text-right">
                  <button className="btn-ghost text-xs text-[var(--error)]" type="button" onClick={() => removeSession(row.id)}><Trash2 size={12} /> Remove</button>
                </td>
              </tr>
            ))}
            {!loading && sessions.length === 0 ? (
              <tr>
                <td className="p-4 text-sm text-[var(--text-secondary)]" colSpan={6}>No sessions yet. Add your first session.</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {!loading && !saving ? (
        <StatusSemanticNote state="readonly" message="Session edits become live in overview, page builder, and onsite capacity views after save." />
      ) : null}
      {error ? <StatusSemanticNote state="error" message={error} /> : null}
    </div>
  );
}
